import { useEffect, useMemo, useState } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Pencil } from "lucide-react";

import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { AuthorizationBusinessUnit } from "../../types/authorizationAdmin";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { withScrollableDataTable } from "./withScrollableDataTable";

const ScrollableDataTable = withScrollableDataTable(DataTable);

const DEFAULT_ROWS_PER_PAGE = 10;
const BUSINESS_UNITS_TABLE_CHROME_OFFSET_PX = 132;

type BusinessUnitsTableProps = {
  readonly businessUnits: AuthorizationBusinessUnit[];
  readonly onEdit: (bu: AuthorizationBusinessUnit) => void;
};

type Row = AuthorizationBusinessUnit & { readonly actions: null };

export const BusinessUnitsTable = ({ businessUnits, onEdit }: BusinessUnitsTableProps) => {
  const [searchText, setSearchText] = useState("");
  const [paginationReset, setPaginationReset] = useState(false);

  useEffect(() => {
    setPaginationReset((t) => !t);
  }, [searchText]);

  const rows = useMemo<Row[]>(
    () => businessUnits.map((bu) => ({ ...bu, actions: null })),
    [businessUnits]
  );

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(
      (r) => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q)
    );
  }, [rows, searchText]);

  const columns: TableColumn<Row>[] = useMemo(
    () => [
      { name: "Code", selector: (row) => row.code, width: "120px" },
      {
        name: "Name",
        selector: (row) => row.name,
        grow: 2,
        wrap: true,
        cell: (row) => (
          <span className="block min-w-0 max-w-full break-words whitespace-normal">{row.name}</span>
        )
      },
      {
        name: "Description",
        selector: (row) => row.description ?? "-",
        grow: 3,
        wrap: true,
        cell: (row) => {
          const text = row.description?.trim() ? row.description : "-";
          return (
            <span className="block min-w-0 max-w-full break-words whitespace-normal" title={row.description ?? undefined}>
              {text}
            </span>
          );
        }
      },
      { name: "Status", selector: (row) => (row.is_active ? "Active" : "Inactive"), width: "100px" },
      {
        name: "",
        width: "100px",
        cell: (row) => (
          <Button variant="ghost" className="!px-2" onClick={() => onEdit(row)} aria-label="Edit business unit">
            <Pencil className="h-4 w-4" />
          </Button>
        ),
        ignoreRowClick: true
      }
    ],
    [onEdit]
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1 space-y-1.5">
          <label htmlFor="business-units-search" className="form-label">
            Search by name or code
          </label>
          <Input
            id="business-units-search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Filter business units…"
            type="search"
            autoComplete="off"
          />
        </div>
      </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ScrollableDataTable
            height="500px"
            chromeOffset={BUSINESS_UNITS_TABLE_CHROME_OFFSET_PX}
            columns={columns}
            data={filteredRows}
            customStyles={dataTableCustomStyles}
            keyField="id"
            pagination
            paginationPerPage={DEFAULT_ROWS_PER_PAGE}
            paginationRowsPerPageOptions={[10, 25, 50]}
            paginationResetDefaultPage={paginationReset}
            noDataComponent={
              <span className="py-6 text-sm text-gray-500">
                {searchText.trim()
                  ? "No business units match your search. Try another name or code."
                  : "No business units configured."}
              </span>
            }
          />
      </div>
    </div>
  );
};
